<div class="stc-wrapper" tabindex="0">
    <div class="stc-container">
        <header class="nf-page-header">
            <button type="button" class="nf-page-header__back" (click)="back()">
                <svg aria-hidden="true" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor"
                    stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M15 6l-6 6 6 6" />
                </svg>
                Back to Traffic Analysis
            </button>
            <div class="nf-page-header__titlerow">
                <h1 class="nf-page-header__title">Create a service</h1>
            </div>
            <p class="nf-page-header__subtitle">
                Review the selected traffic and the services we'll create from it.
            </p>
        </header>

        <!-- Selected traffic preview, from the Traffic Analysis / Microsegments selection. -->
        <section class="nf-panel stc-traffic" aria-label="Selected traffic">
            <div class="stc-traffic__hdr">
                <div class="stc-traffic__lead">
                    <span class="stc-traffic__icon" aria-hidden="true">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
                            stroke-linecap="round" stroke-linejoin="round">
                            <path d="M4 8h13M13 4l4 4-4 4" />
                            <path d="M20 16H7M11 12l-4 4 4 4" />
                        </svg>
                    </span>
                    <h2 class="stc-traffic__title">Selected traffic segments</h2>
                </div>
                @if (observedRange) {
                <span class="stc-daterange">
                    <button type="button" class="stc-daterange__box" [class.is-open]="showCalendar"
                        [attr.aria-expanded]="showCalendar" title="Traffic observed in this window"
                        (click)="toggleCalendar()">
                        <span class="pi pi-calendar stc-daterange__icon" aria-hidden="true"></span>
                        <span class="stc-daterange__text">{{ observedRange }}</span>
                    </button>
                    @if (showCalendar) {
                    <div class="stc-daterange__scrim" (click)="showCalendar = false"></div>
                    <div class="stc-daterange__pop" role="dialog" aria-label="Observed traffic window">
                        <app-date-range-calendar [start]="observedStartMs" [end]="observedEndMs" [readOnly]="true"
                            (cancel)="showCalendar = false"></app-date-range-calendar>
                    </div>
                    }
                </span>
                }
            </div>

            <div class="stc-traffic__group">
                <h3 class="stc-traffic__grouphdr">Source</h3>
                <dl class="stc-traffic__rows">
                    @if (hasSourceDetail) {
                        @if (sourceIdentities.length) {
                        <div class="stc-traffic__row">
                            <dt>Identities</dt>
                            <dd class="stc-chips">
                                @for (id of sourceIdentities; track id) {
                                <span class="stc-chip stc-chip--id">{{ id }}</span>
                                }
                            </dd>
                        </div>
                        }
                        @if (sourceIps.length) {
                        <div class="stc-traffic__row">
                            <dt>Addresses</dt>
                            <dd class="stc-chips">
                                @for (sip of sourceIps; track sip) { <span class="stc-chip">{{ sip }}</span> }
                            </dd>
                        </div>
                        }
                    } @else if (sourceLabel) {
                    <div class="stc-traffic__row">
                        <dt>Name</dt>
                        <dd>{{ sourceLabel }}</dd>
                    </div>
                    }
                </dl>
            </div>

            <div class="stc-traffic__group">
                <h3 class="stc-traffic__grouphdr">Destination</h3>
                <dl class="stc-traffic__rows">
                    @if (destIps.length) {
                    <div class="stc-traffic__row">
                        <dt>Addresses</dt>
                        <dd class="stc-chips">
                            @for (ip of destIps; track ip) { <span class="stc-chip">{{ ip }}</span> }
                        </dd>
                    </div>
                    }
                    @if (destPorts.length) {
                    <div class="stc-traffic__row">
                        <dt>Ports</dt>
                        <dd class="stc-chips">
                            @for (port of destPorts; track port) { <span class="stc-chip">{{ port }}</span> }
                        </dd>
                    </div>
                    }
                    @if (hostIdentities.length) {
                    <div class="stc-traffic__row">
                        <dt>Host</dt>
                        <dd class="stc-chips">
                            @for (host of hostIdentities; track host) {
                            <span class="stc-chip stc-chip--id">{{ host }}</span>
                            }
                        </dd>
                    </div>
                    }
                </dl>
            </div>

        </section>

        <section class="nf-panel stc-setup stc-grouping" aria-label="Choose a setup">
            <div class="stc-setup__hdr">
                <h2 class="stc-setup__title">Choose a setup</h2>
            </div>
            <div class="stc-methods" role="radiogroup" aria-label="Setup">
                <button type="button" class="stc-method" role="radio" [class.is-on]="setup === 'recommended'"
                    [attr.aria-checked]="setup === 'recommended'" (click)="setup = 'recommended'">
                    <span class="stc-method__mark" aria-hidden="true"></span>
                    <span class="stc-method__text">
                        <span class="stc-method__title">Recommended</span>
                        <span class="stc-method__detail">
                            Services configured from your selected traffic, ready to review and create.
                        </span>
                    </span>
                </button>

                <button type="button" class="stc-method" role="radio" [class.is-on]="setup === 'custom'"
                    [attr.aria-checked]="setup === 'custom'" (click)="setup = 'custom'">
                    <span class="stc-method__mark" aria-hidden="true"></span>
                    <span class="stc-method__text">
                        <span class="stc-method__title">Custom</span>
                        <span class="stc-method__detail">
                            Start from a blank service and set it up yourself on the next step.
                        </span>
                    </span>
                </button>
            </div>
        </section>

        @if (setup === 'recommended') {
        <!-- Itemized preview of the services this selection will create. Cards for a handful; a compact
             table once there are more. -->
        <section class="nf-panel stc-setup stc-preview" aria-label="Services to create">
            <div class="stc-preview__hd">
                <div class="stc-preview__lead">
                    <h2 class="stc-setup__title">Services to create</h2>
                    <span class="stc-preview__count">
                        {{ recommendedServices.length }}
                        {{ recommendedServices.length === 1 ? 'service' : 'services' }}
                    </span>
                </div>
                <button type="button" class="stc-preview__diag" (click)="openFlowPreview()">
                    <svg aria-hidden="true" viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor"
                        stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                        <path d="M4 7h7M4 12h10M4 17h5" />
                        <path d="M15 5l5 5-5 5" />
                    </svg>
                    Preview flows &amp; coverage
                </button>
            </div>

            @if (useTablePreview) {
            <div class="stc-preview__well">
                <table class="stc-rvtable">
                    <thead>
                        <tr><th>Destination</th><th>Ports</th><th>Accessed by</th></tr>
                    </thead>
                    <tbody>
                        @for (svc of recommendedServices; track svc.destEndpoint) {
                        <tr>
                            <td><span class="stc-chip stc-chip--dest">{{ svc.destLabel }}</span></td>
                            <td class="stc-rv__ports">{{ svc.portsLabel }}</td>
                            <td class="stc-rv__by">
                                {{ sourceIdentities.length }} identities &middot; {{ sourceIps.length }} addresses
                            </td>
                        </tr>
                        }
                    </tbody>
                </table>
            </div>
            } @else {
            <div class="stc-cards">
                @for (svc of recommendedServices; track svc.destEndpoint) {
                <div class="stc-svc">
                    <div class="stc-svc__hd">
                        <svg class="stc-svc__ico" aria-hidden="true" viewBox="0 0 24 24" fill="none"
                            stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                            <rect x="3" y="4" width="18" height="8" rx="1.5" />
                            <rect x="3" y="14" width="18" height="6" rx="1.5" />
                            <path d="M7 8h.01M7 17h.01" />
                        </svg>
                        <span class="stc-svc__dest">{{ svc.destEndpoint }}</span>
                        @if (svc.multiPort) {
                        <span class="stc-svc__ports">ports {{ svc.portsLabel }}</span>
                        }
                    </div>
                    <dl class="stc-svc__rows">
                        <div class="stc-svc__row">
                            <dt>Accessible by</dt>
                            <dd class="stc-chips">
                                @for (id of sourceIdentities; track id) {
                                <span class="stc-chip stc-chip--id">{{ id }}</span>
                                }
                                @if (sourceIps.length) {
                                <span class="stc-chip stc-chip--more">+{{ sourceIps.length }} addresses</span>
                                }
                            </dd>
                        </div>
                    </dl>
                </div>
                }
            </div>
            }
        </section>
        }

        @if (setup === 'custom') {
        <section class="nf-panel stc-setup" aria-label="Create a service">
            <div class="stc-setup__hdr">
                <h2 class="stc-setup__title">Choose a workflow</h2>
            </div>

            <!-- Method choice: two selectable rows at the top of the combined panel. -->
            <div class="stc-methods" role="radiogroup" aria-label="Service creation method">
                <button type="button" class="stc-method" role="radio" [class.is-on]="selected === 'simple'"
                    [attr.aria-checked]="selected === 'simple'" (click)="select('simple')" (dblclick)="continue()">
                    <span class="stc-method__mark" aria-hidden="true"></span>
                    <span class="stc-method__text">
                        <span class="stc-method__title">Simple</span>
                        <span class="stc-method__detail">Fewer details, using the most commonly used configuration
                            fields.</span>
                    </span>
                </button>

                <button type="button" class="stc-method" role="radio" [class.is-on]="selected === 'template'"
                    [attr.aria-checked]="selected === 'template'" (click)="select('template')" (dblclick)="continue()">
                    <span class="stc-method__mark" aria-hidden="true"></span>
                    <span class="stc-method__text">
                        <span class="stc-method__title">From template</span>
                        <span class="stc-method__detail">Start from a predefined service template and fill in only the
                            required values.</span>
                    </span>
                </button>
            </div>

            @if (selected === 'template') {
            <hr class="stc-setup__divider" aria-hidden="true" />
            <div class="stc-setup__body">
                @if (templatesLoading) {
                <p class="stc-template__muted">Loading templates&hellip;</p>
                } @else if (hasTemplates) {
                <div class="stc-template__field">
                    <label class="stc-shape__lab" for="stc-tpl-search">Service template</label>
                    <input id="stc-tpl-search" class="stc-tpl-search" type="text" autocomplete="off"
                        aria-label="Search templates" placeholder="Search templates&hellip;"
                        [value]="templateSearch" (input)="onTemplateSearch($any($event.target).value)" />
                    <div class="stc-tpl-options" role="radiogroup" aria-label="Service template">
                        @for (t of visibleTemplates; track t.id) {
                        <button type="button" class="stc-tpl-option" role="radio"
                            [class.is-on]="t.id === selectedTemplateId" [attr.aria-checked]="t.id === selectedTemplateId"
                            (click)="selectTemplate(t.id)" (dblclick)="continue()">
                            <span class="stc-tpl-option__mark" aria-hidden="true"></span>
                            <span class="stc-tpl-option__text">
                                <span class="stc-tpl-option__title">{{ t.name }}</span>
                                @if (t.description) { <span class="stc-tpl-option__detail">{{ t.description }}</span> }
                            </span>
                        </button>
                        } @empty {
                        <p class="stc-template__muted">No templates match &ldquo;{{ templateSearch }}&rdquo;</p>
                        }
                    </div>
                    @if (hiddenTemplateCount > 0) {
                    <button type="button" class="stc-tpl-showmore" (click)="showMoreTemplates()">
                        Show more ({{ hiddenTemplateCount }} more)
                        <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
                            stroke-linecap="round" stroke-linejoin="round"><path d="m6 9 6 6 6-6" /></svg>
                    </button>
                    }
                </div>
                } @else {
                <p class="stc-template__muted">No templates yet.
                    <button type="button" class="stc-link" (click)="goToTemplates()">Create a template</button> to use
                    this option.
                </p>
                }
            </div>
            }
        </section>
        }

        <div class="stc-actions">
            <button type="button" class="nf-btn" (click)="back()">Cancel</button>
            <button type="button" class="nf-btn nf-btn--primary" [disabled]="!canContinue" (click)="continue()">
                Continue
            </button>
        </div>
    </div>
</div>
